import { bind } from "@react-rxjs/core"
import { createSignal } from "@react-rxjs/utils"
import {
  defer,
  distinctUntilChanged,
  map,
  merge,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs"

import { priceAndChange$ } from "@/App/Equities/shared/services/marketData/dailyBars"
import { filterNotNull } from "@/App/Equities/shared/utils/filterNotNull"
import { formatOrderAmount } from "@/App/Equities/shared/utils/formatNumber"
import { Direction, OrderStatus, TimeInForce } from "@/generated/TradingGateway"

import { openConfirmDialog } from "../dialog/dialog.service"
import { instrument$ } from "../instruments"
import { positions$ } from "../positions/positions"
import { EquityOrderService, type EquityPosition } from "../TradingGateway"
import {
  confirmClosePosition,
  confirmClosePosition$,
} from "./closePositionSignal"
import { mapRequest, mapResponse } from "./mappingOrders"

export enum OrderType {
  MarketOrder = "market",
  LimitOrder = "limit",
  StopOrder = "stop",
  StopLimitOrder = "stop_limit",
}

type TradeParams = {
  symbol: string
  direction: Direction
  type: OrderType
}

type InstrumentSymbol = string

const [qty$, setQty] = createSignal<string>()
const [limitPrice$, setLimitPrice] = createSignal<string>()
const [stopPrice$, setStopPrice] = createSignal<string>()
const [timeInForce$, setTimeInForce] = createSignal<string>()
const [reset$, reset] = createSignal()
const [tradeParams$, submit] = createSignal<TradeParams>()
const [direction$, toggleDirection] = createSignal<Direction>()
const [orderType$, toggleOrderType] = createSignal<OrderType>()
const [showTradeReceipt$, setShowTradeReceipt] = createSignal<boolean>()
const [closePositionParams$, closePosition] = createSignal<InstrumentSymbol>()

export {
  closePosition,
  confirmClosePosition,
  confirmClosePosition$,
  reset,
  setLimitPrice,
  setTimeInForce as setLimitTimeInForce,
  setQty,
  setShowTradeReceipt,
  setStopPrice,
  submit,
  toggleDirection,
  toggleOrderType,
}

const qtyInput$ = merge(qty$, reset$.pipe(map(() => "")))
const limitPriceInput$ = merge(limitPrice$, reset$.pipe(map(() => "")))
const stopPriceInput$ = merge(stopPrice$, reset$.pipe(map(() => "")))
const timeInForceInput$ = merge(
  timeInForce$,
  reset$.pipe(map(() => TimeInForce.Day)),
)

const [useQtyValue, qtyValue$] = bind(qtyInput$, "")
const [useLimitPriceValue, limitPriceValue$] = bind(limitPriceInput$, "")
const [useStopPriceValue, stopPriceValue$] = bind(stopPriceInput$, "")
const [useTimeInForceValue, timeInForceValue$] = bind(
  timeInForceInput$,
  TimeInForce.Day,
)

const [useIndicativeCostValue] = bind((symbol: string) => {
  return qtyValue$.pipe(
    distinctUntilChanged(),
    switchMap((qty) =>
      priceAndChange$(symbol).pipe(
        map((priceChange) => {
          if (qty) {
            const value = Number.parseFloat(qty) * priceChange.price
            return formatOrderAmount(value)
          }
          return "0"
        }),
      ),
    ),
  )
}, "0")

export {
  useIndicativeCostValue,
  useLimitPriceValue,
  useQtyValue,
  useStopPriceValue,
  useTimeInForceValue,
}

export const closePositionDialog$ = closePositionParams$.pipe(
  filterNotNull(),
  switchMap((symbol) =>
    instrument$(symbol).pipe(
      take(1),
      tap((instrument) => {
        const title = `Are you sure you want to close position for '${
          instrument?.displayName ?? symbol
        }'?`

        openConfirmDialog({
          title,
          confirmLabel: `Close ${symbol}`,
          onConfirm: () => confirmClosePosition(symbol),
        })
      }),
    ),
  ),
)

const closePositionResponse$ = confirmClosePosition$.pipe(
  withLatestFrom(positions$),
  map(([symbol, positions]) =>
    positions.find((position) => position?.symbol === symbol),
  ),
  filterNotNull(),
  switchMap((params: EquityPosition) => {
    return OrderService.placeOrder$({
      symbol: params.symbol,
      side: Direction.Sell,
      type: OrderType.MarketOrder,
      qty: params.qty,
    })
  }),
)

export const [useClosePositionResponse, _closePositionResponse$] =
  bind<Order | null>(closePositionResponse$, null)

const response$ = tradeParams$.pipe(
  withLatestFrom(
    qtyValue$,
    limitPriceValue$,
    timeInForceValue$,
    stopPriceValue$,
  ),
  switchMap(([tradeParams, qty, limitPrice, timeInForce, stopPrice]) => {
    if (tradeParams.type === OrderType.MarketOrder) {
      return OrderService.placeOrder$({
        ...tradeParams,
        side: tradeParams.direction,
        type: tradeParams.type,
        qty,
      })
    } else if (tradeParams.type === OrderType.LimitOrder) {
      const message: LimitOrderRequest = {
        ...tradeParams,
        type: tradeParams.type,
        side: tradeParams.direction,
        qty,
        limitPrice,
        timeInForce,
      }
      return OrderService.placeOrder$(message)
    } else {
      const message: StopOrderRequest = {
        ...tradeParams,
        type: limitPrice ? OrderType.StopLimitOrder : tradeParams.type,
        side: tradeParams.direction,
        qty,
        timeInForce,
        stopPrice,
        limitPrice,
      }
      return OrderService.placeOrder$(message)
    }
  }),
)

export const [useResponse, responseStream$] = bind<{
  isLoading: boolean
  response?: Order
}>(
  merge(
    tradeParams$.pipe(map(() => ({ isLoading: true }))),
    response$.pipe(
      map((response) => ({
        isLoading: false,
        response,
      })),
    ),
  ),
  { isLoading: false },
)

export const [useOrderType] = bind<OrderType>(orderType$, OrderType.MarketOrder)

export const [useTradeDirection, tradeDirectionStream$] = bind<Direction>(
  direction$.pipe(
    map((direction) =>
      direction === Direction.Buy ? Direction.Buy : Direction.Sell,
    ),
  ),
  Direction.Buy,
)

export const [useShowTradeReceipt] = bind<boolean>(
  showTradeReceipt$.pipe(map((showTradeReceipt) => showTradeReceipt)),
  false,
)

type OrderRequest = {
  symbol: string
  qty: string
  side: Direction
  type: OrderType
}

export type StopOrderRequest = OrderRequest & {
  timeInForce: string
  stopPrice: string
  limitPrice?: string
  type: OrderType.StopOrder | OrderType.StopLimitOrder
}

export type LimitOrderRequest = OrderRequest & {
  limitPrice: string
  timeInForce: string
  type: OrderType.LimitOrder
}

export type MarketOrderRequest = OrderRequest & {
  type: OrderType.MarketOrder
}

interface OrderResponse {
  symbol: string
  qty: string
  side: string
  commission: string
  clientOrderId: string
  status: string
  createdAt: string
}

export interface Order {
  type: OrderStatus
  order: OrderResponse
  error?: {
    type: string
    message: string
    msg?: {
      code: number
      message: string
    }
  }
}

export const OrderService = {
  placeOrder$: (
    req: MarketOrderRequest | LimitOrderRequest | StopOrderRequest,
  ) => {
    return defer(() => EquityOrderService.createOrder(mapRequest(req))).pipe(
      map(mapResponse),
    )
  },
}
